import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import type {
  Journey,
  JourneyWeek,
  JourneyActivity,
  ActivityProgress,
  JourneyStats,
  CreateJourneyInput,
  CompletionInput,
} from '@coffeeplexity/shared';
import { offlineJourneyService } from '../../services/OfflineJourneyService';

interface JourneyState {
  journeys: Journey[];
  currentJourney: Journey | null;
  currentWeek: JourneyWeek | null;
  currentActivity: JourneyActivity | null;
  journeyStats: JourneyStats | null;
  recommendations: JourneyActivity[];
  loading: {
    journeys: boolean;
    journey: boolean;
    week: boolean;
    activity: boolean;
    stats: boolean;
    recommendations: boolean;
    creating: boolean;
    completing: boolean;
  };
  error: string | null;
  // Offline state
  isOnline: boolean;
  hasCachedData: boolean;
  pendingSync: number;
  lastSyncAt: string | null;
}

const initialState: JourneyState = {
  journeys: [],
  currentJourney: null,
  currentWeek: null,
  currentActivity: null,
  journeyStats: null,
  recommendations: [],
  loading: {
    journeys: false,
    journey: false,
    week: false,
    activity: false,
    stats: false,
    recommendations: false,
    creating: false,
    completing: false,
  },
  error: null,
  // Offline state
  isOnline: false,
  hasCachedData: false,
  pendingSync: 0,
  lastSyncAt: null,
};

// Async thunks
export const fetchMyJourneys = createAsyncThunk(
  'journey/fetchMyJourneys',
  async (userId: string, { rejectWithValue }) => {
    try {
      return await offlineJourneyService.getMyJourneys(userId);
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch journeys');
    }
  }
);

export const fetchJourney = createAsyncThunk(
  'journey/fetchJourney',
  async (journeyId: string, { rejectWithValue }) => {
    try {
      const journey = await offlineJourneyService.getJourney(journeyId);
      if (!journey) {
        return rejectWithValue('Journey not found');
      }
      return journey;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch journey');
    }
  }
);

export const fetchJourneyWeek = createAsyncThunk(
  'journey/fetchJourneyWeek',
  async (
    { journeyId, weekNumber }: { journeyId: string; weekNumber: number },
    { rejectWithValue }
  ) => {
    try {
      const week = await offlineJourneyService.getJourneyWeek(
        journeyId,
        weekNumber
      );
      if (!week) {
        return rejectWithValue('Journey week not found');
      }
      return week;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch journey week');
    }
  }
);

export const fetchJourneyActivity = createAsyncThunk(
  'journey/fetchJourneyActivity',
  async (activityId: string, { rejectWithValue }) => {
    try {
      const activity =
        await offlineJourneyService.getJourneyActivity(activityId);
      if (!activity) {
        return rejectWithValue('Activity not found');
      }
      return activity;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch activity');
    }
  }
);

export const createJourney = createAsyncThunk(
  'journey/createJourney',
  async (input: CreateJourneyInput, { rejectWithValue }) => {
    try {
      return await offlineJourneyService.createJourney(input);
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to create journey');
    }
  }
);

export const completeActivity = createAsyncThunk(
  'journey/completeActivity',
  async (
    {
      activityId,
      payload,
      userId,
    }: {
      activityId: string;
      payload: CompletionInput;
      userId?: string;
    },
    { rejectWithValue }
  ) => {
    try {
      return await offlineJourneyService.completeActivity(
        activityId,
        payload,
        userId || 'current_user'
      );
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to complete activity');
    }
  }
);

export const fetchJourneyStats = createAsyncThunk(
  'journey/fetchJourneyStats',
  async (journeyId: string, { rejectWithValue }) => {
    try {
      const stats = await offlineJourneyService.getJourneyStats(journeyId);
      if (!stats) {
        return rejectWithValue('Journey stats not available offline');
      }
      return stats;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch journey stats');
    }
  }
);

export const fetchRecommendations = createAsyncThunk(
  'journey/fetchRecommendations',
  async (
    { journeyId, limit = 5 }: { journeyId: string; limit?: number },
    { rejectWithValue }
  ) => {
    try {
      return await offlineJourneyService.getRecommendations(journeyId, limit);
    } catch (error: any) {
      return rejectWithValue(
        error.message || 'Failed to fetch recommendations'
      );
    }
  }
);

// New thunk for updating activity progress
export const updateActivityProgress = createAsyncThunk(
  'journey/updateActivityProgress',
  async (
    {
      activityId,
      progressPercentage,
      userId,
    }: {
      activityId: string;
      progressPercentage: number;
      userId?: string;
    },
    { rejectWithValue }
  ) => {
    try {
      return await offlineJourneyService.updateActivityProgress(
        activityId,
        progressPercentage,
        userId || 'current_user'
      );
    } catch (error: any) {
      return rejectWithValue(
        error.message || 'Failed to update activity progress'
      );
    }
  }
);

// New thunk for getting offline status
export const fetchOfflineStatus = createAsyncThunk(
  'journey/fetchOfflineStatus',
  async (_, { rejectWithValue }) => {
    try {
      return await offlineJourneyService.getOfflineStatus();
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch offline status');
    }
  }
);

// New thunk for forcing sync
export const forceSyncAll = createAsyncThunk(
  'journey/forceSyncAll',
  async (_, { rejectWithValue }) => {
    try {
      await offlineJourneyService.forceSyncAll();
      return true;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to sync data');
    }
  }
);

const journeySlice = createSlice({
  name: 'journey',
  initialState,
  reducers: {
    clearError: state => {
      state.error = null;
    },
    clearCurrentJourney: state => {
      state.currentJourney = null;
      state.currentWeek = null;
      state.currentActivity = null;
      state.journeyStats = null;
    },
    clearCurrentWeek: state => {
      state.currentWeek = null;
      state.currentActivity = null;
    },
    clearCurrentActivity: state => {
      state.currentActivity = null;
    },
    updateActivityProgressLocal: (
      state,
      action: PayloadAction<{ activityId: string; progress: ActivityProgress }>
    ) => {
      const { activityId, progress } = action.payload;

      // Update in current activity
      if (state.currentActivity?.id === activityId) {
        state.currentActivity.progress = progress;
        state.currentActivity.is_completed = progress.status === 'completed';
      }

      // Update in current week activities
      if (state.currentWeek) {
        const activity = state.currentWeek.activities.find(
          a => a.id === activityId
        );
        if (activity) {
          activity.progress = progress;
          activity.is_completed = progress.status === 'completed';
        }
      }

      // Update in current journey
      if (state.currentJourney) {
        for (const week of state.currentJourney.weeks) {
          const activity = week.activities.find(a => a.id === activityId);
          if (activity) {
            activity.progress = progress;
            activity.is_completed = progress.status === 'completed';
            break;
          }
        }
      }
    },
    updateOfflineStatus: (
      state,
      action: PayloadAction<{
        isOnline: boolean;
        hasCachedData: boolean;
        pendingSync: number;
        lastSyncAt: string | null;
      }>
    ) => {
      const { isOnline, hasCachedData, pendingSync, lastSyncAt } =
        action.payload;
      state.isOnline = isOnline;
      state.hasCachedData = hasCachedData;
      state.pendingSync = pendingSync;
      state.lastSyncAt = lastSyncAt;
    },
  },
  extraReducers: builder => {
    // Fetch my journeys
    builder
      .addCase(fetchMyJourneys.pending, state => {
        state.loading.journeys = true;
        state.error = null;
      })
      .addCase(fetchMyJourneys.fulfilled, (state, action) => {
        state.loading.journeys = false;
        state.journeys = action.payload;
      })
      .addCase(fetchMyJourneys.rejected, (state, action) => {
        state.loading.journeys = false;
        state.error = action.payload as string;
      });

    // Fetch journey
    builder
      .addCase(fetchJourney.pending, state => {
        state.loading.journey = true;
        state.error = null;
      })
      .addCase(fetchJourney.fulfilled, (state, action) => {
        state.loading.journey = false;
        state.currentJourney = action.payload;
      })
      .addCase(fetchJourney.rejected, (state, action) => {
        state.loading.journey = false;
        state.error = action.payload as string;
      });

    // Fetch journey week
    builder
      .addCase(fetchJourneyWeek.pending, state => {
        state.loading.week = true;
        state.error = null;
      })
      .addCase(fetchJourneyWeek.fulfilled, (state, action) => {
        state.loading.week = false;
        state.currentWeek = action.payload;
      })
      .addCase(fetchJourneyWeek.rejected, (state, action) => {
        state.loading.week = false;
        state.error = action.payload as string;
      });

    // Fetch journey activity
    builder
      .addCase(fetchJourneyActivity.pending, state => {
        state.loading.activity = true;
        state.error = null;
      })
      .addCase(fetchJourneyActivity.fulfilled, (state, action) => {
        state.loading.activity = false;
        state.currentActivity = action.payload;
      })
      .addCase(fetchJourneyActivity.rejected, (state, action) => {
        state.loading.activity = false;
        state.error = action.payload as string;
      });

    // Create journey
    builder
      .addCase(createJourney.pending, state => {
        state.loading.creating = true;
        state.error = null;
      })
      .addCase(createJourney.fulfilled, (state, action) => {
        state.loading.creating = false;
        state.journeys.unshift(action.payload);
        state.currentJourney = action.payload;
      })
      .addCase(createJourney.rejected, (state, action) => {
        state.loading.creating = false;
        state.error = action.payload as string;
      });

    // Complete activity
    builder
      .addCase(completeActivity.pending, state => {
        state.loading.completing = true;
        state.error = null;
      })
      .addCase(completeActivity.fulfilled, (state, action) => {
        state.loading.completing = false;
        // Update activity progress in state
        if (state.currentActivity) {
          state.currentActivity.progress = action.payload;
          state.currentActivity.is_completed =
            action.payload.status === 'completed';
        }
      })
      .addCase(completeActivity.rejected, (state, action) => {
        state.loading.completing = false;
        state.error = action.payload as string;
      });

    // Fetch journey stats
    builder
      .addCase(fetchJourneyStats.pending, state => {
        state.loading.stats = true;
        state.error = null;
      })
      .addCase(fetchJourneyStats.fulfilled, (state, action) => {
        state.loading.stats = false;
        state.journeyStats = action.payload;
      })
      .addCase(fetchJourneyStats.rejected, (state, action) => {
        state.loading.stats = false;
        state.error = action.payload as string;
      });

    // Fetch recommendations
    builder
      .addCase(fetchRecommendations.pending, state => {
        state.loading.recommendations = true;
        state.error = null;
      })
      .addCase(fetchRecommendations.fulfilled, (state, action) => {
        state.loading.recommendations = false;
        state.recommendations = action.payload;
      })
      .addCase(fetchRecommendations.rejected, (state, action) => {
        state.loading.recommendations = false;
        state.error = action.payload as string;
      });

    // Update activity progress
    builder
      .addCase(updateActivityProgress.pending, state => {
        // Don't show loading for progress updates (optimistic)
      })
      .addCase(updateActivityProgress.fulfilled, (state, action) => {
        // Update local state with the progress
        const progress = action.payload;
        if (state.currentActivity?.id === progress.activity_id) {
          state.currentActivity.progress = progress;
        }
      })
      .addCase(updateActivityProgress.rejected, (state, action) => {
        state.error = action.payload as string;
      });

    // Fetch offline status
    builder.addCase(fetchOfflineStatus.fulfilled, (state, action) => {
      const { isOnline, hasCachedData, pendingSync, lastSyncAt } =
        action.payload;
      state.isOnline = isOnline;
      state.hasCachedData = hasCachedData;
      state.pendingSync = pendingSync;
      state.lastSyncAt = lastSyncAt;
    });

    // Force sync all
    builder
      .addCase(forceSyncAll.pending, state => {
        // Could add a syncing flag if needed
      })
      .addCase(forceSyncAll.fulfilled, state => {
        // Sync completed, could refresh offline status
        state.pendingSync = 0;
      })
      .addCase(forceSyncAll.rejected, (state, action) => {
        state.error = action.payload as string;
      });
  },
});

export const {
  clearError,
  clearCurrentJourney,
  clearCurrentWeek,
  clearCurrentActivity,
  updateActivityProgressLocal,
  updateOfflineStatus,
} = journeySlice.actions;

export default journeySlice.reducer;
